<template>
  <teleport to="body">
    <div
      v-if="modelValue"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content q-modal">
          <div class="modal-header border-0 pb-2" style="display: flex; justify-content: space-between; align-items:center">
            <div>
              <h5 class="modal-title fw-bold mb-1 text-white">How it works</h5>
              <div class="text-white-50 fw-semibold">
                Upload → Analyze → Get quizzes, flashcards & a study plan
              </div>
            </div>

            <button type="button" class="btn btn-sm q-x" @click="close" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="modal-body pt-2">
            <div class="q-demo rounded-4 p-3">
              <div class="q-demo-screen rounded-4 overflow-hidden position-relative">
                <!-- Top progress + status -->
                <div class="q-topbar">
                  <div class="q-progress">
                    <div class="q-progress-bar" :style="{ width: progress + '%' }"></div>
                  </div>

                  <div class="d-flex align-items-center justify-content-between mt-2">
                    <div class="text-white-50 fw-semibold">
                      {{ finished ? "Results ready" : stepText }}
                    </div>

                    <div class="d-flex gap-2">
                      <button
                        class="btn btn-sm q-pillbtn"
                        type="button"
                        @click="replay"
                        :disabled="!started"
                      >
                        <i class="bi bi-arrow-repeat me-2"></i>Replay
                      </button>

                      <button
                        v-if="finished"
                        class="btn btn-sm q-pillbtn"
                        type="button"
                        @click="downloadQuiz"
                      >
                        <i class="bi bi-download me-2"></i>Download quiz
                      </button>
                    </div>
                  </div>
                </div>

                <!-- PLAYING / RESULTS area -->
                <div class="q-stage-wrap">
                  <!-- PLAYING -->
                  <div v-if="!finished" class="q-stage">
                    <div class="q-scan" aria-hidden="true"></div>

                    <div class="q-cards" aria-hidden="true">
                      <div class="q-card"></div>
                      <div class="q-card q-card-mid"></div>
                      <div class="q-card"></div>
                    </div>

                    <button class="btn q-play" type="button" @click="startOrFinish">
                      <i class="bi" :class="playing ? 'bi-skip-forward-fill' : 'bi-play-fill'"></i>
                    </button>

                    <div class="q-hint text-white-50 fw-semibold">
                      {{ playing ? "Click to finish demo" : "Click to play demo" }}
                    </div>
                  </div>

                  <!-- RESULTS -->
                  <div v-else class="q-results p-3 q-fadein">
                    <div class="d-flex flex-wrap gap-2 mb-3">
                      <a class="btn btn-sm q-pillbtn q-pillbtn-grad" href="/notes">
                        <i class="bi bi-magic me-2"></i>Try Analyze Notes
                      </a>
                    </div>

                    <div class="row g-3">
                      <div class="col-12 col-lg-4">
                        <div class="q-box rounded-4 p-3 h-100">
                          <div class="fw-bold text-white mb-2">Summary</div>
                          <ul class="text-white-50 fw-semibold mb-0 ps-3">
                            <li v-for="(s,i) in demo.summary" :key="i">{{ s }}</li>
                          </ul>
                        </div>
                      </div>

                      <div class="col-12 col-lg-4">
                        <div class="q-box rounded-4 p-3 h-100">
                          <div class="fw-bold text-white mb-2">Mini Quiz</div>
                          <div class="text-white fw-semibold mb-2">{{ demo.quiz.q }}</div>
                          <div class="d-grid gap-2">
                            <div v-for="(o,i) in demo.quiz.options" :key="i" class="q-opt">
                              <span class="q-dot"></span>
                              <span class="text-white-50 fw-semibold">{{ o }}</span>
                            </div>
                          </div>
                          <div class="mt-2 text-white fw-semibold">
                            Answer: <span class="q-gradtext">{{ demo.quiz.answer }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="col-12 col-lg-4">
                        <div class="q-box rounded-4 p-3 h-100">
                          <div class="fw-bold text-white mb-2">Study Plan</div>
                          <div v-for="(d,i) in demo.plan" :key="i" class="q-day">
                            <div>
                              <div class="text-white fw-semibold">{{ d.day }}</div>
                              <div class="text-white-50 fw-semibold small">{{ d.focus }}</div>
                            </div>
                            <span class="q-time">{{ d.time }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Steps -->
              <div class="row g-3 mt-3">
                <div class="col-12 col-md-4">
                  <div class="q-stepbox p-3 rounded-4">
                    <div class="q-ic"><i class="bi bi-cloud-arrow-up"></i></div>
                    <div class="text-white fw-bold mt-2">Upload</div>
                    <div class="text-white-50 fw-semibold small">Image or PDF</div>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <div class="q-stepbox p-3 rounded-4">
                    <div class="q-ic"><i class="bi bi-magic"></i></div>
                    <div class="text-white fw-bold mt-2">Analyze</div>
                    <div class="text-white-50 fw-semibold small">Extract key concepts</div>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <div class="q-stepbox p-3 rounded-4">
                    <div class="q-ic"><i class="bi bi-check2-circle"></i></div>
                    <div class="text-white fw-bold mt-2">Study</div>
                    <div class="text-white-50 fw-semibold small">Quiz + plan</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 pt-0">
            <button class="btn q-pillbtn" type="button" @click="close">Got it</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="modelValue" class="q-backdrop"></div>
  </teleport>
</template>

<script>
export default {
  name: "HowItWorksModal",
  props: { modelValue: { type: Boolean, default: false } },
  emits: ["update:modelValue"],
  data() {
    return {
      started: false,
      playing: false,
      finished: false,
      stepIndex: 0,
      timer: null,
      progressTimer: null,
      progress: 0,
      steps: ["Uploading notes…", "Analyzing content…", "Generating quiz…", "Building study plan…", "Finalizing…"],
      demo: {
        summary: [
          "Active recall beats passive reading.",
          "Use spaced repetition to retain concepts.",
          "Turn headings into questions to self-test fast.",
        ],
        quiz: {
          q: "Which technique improves retention most?",
          options: ["Highlighting", "Active recall", "Re-reading", "Copying notes"],
          answer: "Active recall",
        },
        plan: [
          { day: "Day 1", focus: "Extract key terms + 10 flashcards", time: "30–45 min" },
          { day: "Day 2", focus: "Mini quiz + review mistakes", time: "25–30 min" },
          { day: "Day 3", focus: "Mixed practice + recap", time: "30 min" },
        ],
      },
    };
  },
  computed: {
    stepText() {
      return this.steps[this.stepIndex] || "Working…";
    },
    target() {
      const max = Math.max(this.steps.length - 1, 1);
      return Math.round((this.stepIndex / max) * 100);
    },
    quizAsText() {
      const q = this.demo.quiz;
      const opts = q.options.map((o) => `- ${o}`).join("\n");
      return `Quick Quiz AI — Demo Quiz\n\nQ: ${q.q}\n\n${opts}\n\nAnswer: ${q.answer}\n`;
    },
  },
  methods: {
    close() {
      this.stop();
      this.$emit("update:modelValue", false);
    },
    startOrFinish() {
      if (!this.started) this.start();
      else this.finish();
    },
    start() {
      this.stopTimersOnly();
      this.started = true;
      this.playing = true;
      this.finished = false;
      this.stepIndex = 0;
      this.progress = 0;

      this.timer = setInterval(() => {
        if (this.stepIndex < this.steps.length - 1) this.stepIndex += 1;
        else this.finish();
      }, 850);

      this.progressTimer = setInterval(() => {
        const diff = this.target - this.progress;
        this.progress = Math.abs(diff) < 0.6 ? this.target : this.progress + diff * 0.22;
      }, 40);
    },
    finish() {
      this.playing = false;
      this.finished = true;
      this.progress = 100;
      this.stopTimersOnly();
    },
    replay() {
      this.started = false;
      this.playing = false;
      this.finished = false;
      this.stepIndex = 0;
      this.progress = 0;
      this.start();
    },
    stopTimersOnly() {
      if (this.timer) clearInterval(this.timer);
      if (this.progressTimer) clearInterval(this.progressTimer);
      this.timer = null;
      this.progressTimer = null;
    },
    stop() {
      this.stopTimersOnly();
      this.started = false;
      this.playing = false;
      this.finished = false;
      this.stepIndex = 0;
      this.progress = 0;
    },
    downloadQuiz() {
      const now = new Date().toISOString().slice(0, 10);
      const blob = new Blob([this.quizAsText], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quick-quiz-ai-demo-quiz-${now}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  },
  beforeUnmount() {
    this.stop();
  },
};
</script>

<style scoped>
.q-modal{
  background: rgba(12,14,22,0.88);
  border: 1px solid rgba(255,255,255,0.10);
  box-shadow: 0 30px 80px rgba(0,0,0,0.55);
  border-radius: 1.25rem;
}
.q-x{
  border-radius: 0.9rem;
  border: 1px solid rgba(255,255,255,0.14);
  background: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.9);
}
.q-x:hover{ background: rgba(255,255,255,0.07); }

.q-demo{ background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); }
.q-demo-screen{
  min-height: 22rem;
  background: radial-gradient(circle at 70% 20%, rgba(124,58,237,0.16), transparent 55%),
              radial-gradient(circle at 25% 75%, rgba(255,61,141,0.12), transparent 55%),
              rgba(0,0,0,0.22);
  border: 1px solid rgba(255,255,255,0.10);
}

.q-topbar{ padding: 1rem 1rem 0 1rem; }
.q-progress{
  height: 0.5rem;
  border-radius: 999px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.10);
  overflow: hidden;
}
.q-progress-bar{
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg,#2d6cff 0%,#7c3aed 55%,#ff3d8d 120%);
  transition: width 120ms ease;
}

.q-stage-wrap{ padding: 1rem; }
.q-stage{
  position: relative;
  height: 15rem;
  border-radius: 1.25rem;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(0,0,0,0.18);
  overflow: hidden;
  display: grid;
  place-items: center;
}

.q-scan{
  position: absolute;
  inset: -40% -50%;
  background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.08), transparent 70%);
  transform: translateX(-35%);
  animation: scan 1.6s linear infinite;
  pointer-events: none;
}
@keyframes scan{
  to { transform: translateX(35%); }
}

.q-cards{
  position: absolute;
  inset: 2.25rem 2rem;
  display: grid;
  grid-template-columns: repeat(3,1fr);
  gap: 0.9rem;
  align-items: end;
  pointer-events: none;
}
.q-card{
  height: 7rem;
  border-radius: 1rem;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.10);
  animation: floaty 2.1s ease-in-out infinite;
}
.q-card-mid{ height: 9.8rem; animation-delay: .12s; }
.q-card:nth-child(3){ animation-delay: .24s; }
@keyframes floaty{
  0%,100%{ transform: translateY(0); }
  50%{ transform: translateY(-0.45rem); }
}

.q-play{
  width: 4.1rem; height: 4.1rem;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.16);
  background: rgba(0,0,0,0.35);
  color: rgba(255,255,255,0.95);
  box-shadow: 0 16px 40px rgba(0,0,0,0.35);
  z-index: 2;
}
.q-play i{ font-size: 1.55rem; }

.q-hint{ position: absolute; bottom: .9rem; left: 1rem; }

.q-fadein{ animation: fadein 160ms ease-out both; }
@keyframes fadein{ from{ opacity:0; transform: translateY(6px);} to{ opacity:1; transform: translateY(0);} }

.q-pillbtn{
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.16);
  background: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.92);
  font-weight: 800;
}
.q-pillbtn:hover{ background: rgba(255,255,255,0.07); }
.q-pillbtn:disabled{ opacity: 0.5; }

.q-pillbtn-grad{
  border: 0;
  background: linear-gradient(90deg,#2d6cff 0%,#7c3aed 55%,#ff3d8d 120%);
  box-shadow: 0 16px 40px rgba(0,0,0,0.35);
}
.q-pillbtn-grad:hover{ filter: brightness(1.06); }

.q-box{
  background: rgba(0,0,0,0.22);
  border: 1px solid rgba(255,255,255,0.10);
}

.q-opt{
  display:flex; align-items:center; gap:.55rem;
  padding: .55rem .65rem;
  border-radius: .9rem;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
}
.q-dot{ width:.45rem; height:.45rem; border-radius:999px; background: rgba(255,255,255,0.55); }

.q-day{
  display:flex;
  align-items:flex-start;         /* <-- keep top aligned */
  justify-content:space-between;
  gap:.75rem;
  padding: .65rem .7rem;
  border-radius: .95rem;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.10);
  margin-bottom: .55rem;
}

.q-time{
  flex: 0 0 auto;                 /* <-- DO NOT shrink */
  min-width: 5.6rem;              /* <-- consistent pill width */
  text-align: center;             /* <-- centered text */
  padding: .28rem .7rem;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.16);
  background: rgba(0,0,0,0.32);   /* <-- stronger contrast */
  color: rgba(255,255,255,0.86);
  font-weight: 900;
  font-size: .85rem;
  white-space: nowrap;            /* <-- NEVER wrap 30–45 min */
  line-height: 1;                 /* <-- prevents weird vertical centering */
}


.q-stepbox{
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
}
.q-ic{
  width: 2.6rem; height: 2.6rem;
  border-radius: 1.05rem;
  display:grid; place-items:center;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.10);
  color: rgba(255,255,255,0.9);
  font-size: 1.1rem;
}

.q-gradtext{
  background: linear-gradient(90deg,#2d6cff 0%,#7c3aed 55%,#ff3d8d 120%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.q-backdrop{
  position: fixed;
  inset: 0;
  z-index: 1040; /* bootstrap backdrop level */
  background: rgba(16, 15, 15, 0.72);   /* darker */
  backdrop-filter: blur(5px);    
  -webkit-backdrop-filter: blur(18px);
}
.modal{
  z-index: 1050;
}

</style>
